import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import TimerIcon from '@mui/icons-material/Timer';
import { Box, Button, ButtonBase, Grid, TableSortLabel, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { FileTypes } from '../../utils/constants';
import TaskListItem, { TASK_COLUMN_LABELS, TASK_COLUMN_WIDTHS } from '../tasks/TaskListItem';
import { groupRows, rowKey, upcomingLabel } from '../tasks/taskUi';
import FileListItem from './FileListItem';
import FolderListItem from './FolderListItem';

const ListView = ({
    folders,
    videoFiles,
    textFiles,
    audioFiles,
    bookFiles,
    onFolderClick,
    onVideoFileClick,
    onTextFileClick,
    onAudioFileClick,
    onBookFileClick,
    onMenuOpen,
    showPaths = false,
    isSelectMode = false,
    selectedFiles = new Map(),
    onSelectToggle,
    // Task mode: one row per (file, language). Presentational only; the caller fetches.
    rowMode = 'file',
    taskRows = [],
    taskColumns = [],
    groups = null,
    currentUser = null,
    onTaskOpen,
    // Optional task selection (PM bulk actions): ids of selected tasks, and a toggle that turns it on
    selectedTaskIds = null,
    onTaskSelectToggle,
    // Optional link beside a group header, e.g. Workload's "Open their tasks"
    onGroupOpen,
    groupOpenLabel = 'Open',
    emptyTitle,
    emptyMessage,
    // Optional controlled sort; when omitted the list sorts in memory as before
    order: controlledOrder,
    orderBy: controlledOrderBy,
    onRequestSort,
}) => {
    const theme = useTheme(); // Get current theme to check dark mode
    const hasItems =
        (folders && folders.length > 0) ||
        (videoFiles && videoFiles.length > 0) ||
        (textFiles && textFiles.length > 0) ||
        (audioFiles && audioFiles.length > 0) ||
        (bookFiles && bookFiles.length > 0);

    // Add state for sorting
    const [localOrder, setOrder] = useState('asc');
    const [localOrderBy, setOrderBy] = useState('name');
    const order = controlledOrder ?? localOrder;
    const orderBy = controlledOrderBy ?? localOrderBy;
    const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

    // Handle sort request
    const handleRequestSort = (property) => {
        if (onRequestSort) {
            onRequestSort(property);
            return;
        }
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Sort function for folders
    const sortedFolders = React.useMemo(() => {
        if (!folders || folders.length === 0) return [];

        return [...folders].sort((a, b) => {
            // Always sort folders by the selected criteria
            switch (orderBy) {
                case 'name':
                    return order === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
                case 'updatedBy':
                    const updatedByA = a.updatedBy || 'Unknown';
                    const updatedByB = b.updatedBy || 'Unknown';
                    return order === 'asc'
                        ? updatedByA.localeCompare(updatedByB)
                        : updatedByB.localeCompare(updatedByA);
                case 'updatedAt':
                    const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    return order === 'asc' ? dateA - dateB : dateB - dateA;
                default:
                    return 0;
            }
        });
    }, [folders, order, orderBy]);

    // Sort function for files
    const sortedVideoFiles = React.useMemo(() => {
        if (!videoFiles || videoFiles.length === 0) return [];

        return [...videoFiles].sort((a, b) => {
            switch (orderBy) {
                case 'name':
                    const nameA = a.name || a.videoName || '';
                    const nameB = b.name || b.videoName || '';
                    return order === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
                case 'duration':
                    // Parse duration format "HH:MM:SS" to seconds for comparison
                    const getDurationInSeconds = (duration) => {
                        if (!duration) return 0;
                        const parts = duration.split(':').map(Number);
                        if (parts.length === 3) {
                            // HH:MM:SS
                            return parts[0] * 3600 + parts[1] * 60 + parts[2];
                        } else if (parts.length === 2) {
                            // MM:SS
                            return parts[0] * 60 + parts[1];
                        }
                        return 0;
                    };

                    const durationA = getDurationInSeconds(a.duration);
                    const durationB = getDurationInSeconds(b.duration);
                    return order === 'asc' ? durationA - durationB : durationB - durationA;
                case 'updatedBy':
                    const updatedByA = a.updatedBy || a.lastModifiedBy || 'Unknown';
                    const updatedByB = b.updatedBy || b.lastModifiedBy || 'Unknown';
                    return order === 'asc'
                        ? updatedByA.localeCompare(updatedByB)
                        : updatedByB.localeCompare(updatedByA);
                case 'updatedAt':
                    const dateA =
                        a.updatedAt || a.lastModifiedDate ? new Date(a.updatedAt || a.lastModifiedDate).getTime() : 0;
                    const dateB =
                        b.updatedAt || b.lastModifiedDate ? new Date(b.updatedAt || b.lastModifiedDate).getTime() : 0;
                    return order === 'asc' ? dateA - dateB : dateB - dateA;
                case 'languages':
                    // Sort by number of languages
                    const langCountA = a.languages?.length || 0;
                    const langCountB = b.languages?.length || 0;
                    return order === 'asc' ? langCountA - langCountB : langCountB - langCountA;
                default:
                    return 0;
            }
        });
    }, [videoFiles, order, orderBy]);

    const sortedTextFiles = React.useMemo(() => {
        if (!textFiles || textFiles.length === 0) return [];

        return [...textFiles].sort((a, b) => {
            switch (orderBy) {
                case 'name':
                    const nameA = a.name || a.videoName || '';
                    const nameB = b.name || b.videoName || '';
                    return order === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
                case 'updatedBy':
                    const updatedByA = a.updatedBy || a.lastModifiedBy || 'Unknown';
                    const updatedByB = b.updatedBy || b.lastModifiedBy || 'Unknown';
                    return order === 'asc'
                        ? updatedByA.localeCompare(updatedByB)
                        : updatedByB.localeCompare(updatedByA);
                case 'updatedAt':
                    const dateA =
                        a.updatedAt || a.lastModifiedDate ? new Date(a.updatedAt || a.lastModifiedDate).getTime() : 0;
                    const dateB =
                        b.updatedAt || b.lastModifiedDate ? new Date(b.updatedAt || b.lastModifiedDate).getTime() : 0;
                    return order === 'asc' ? dateA - dateB : dateB - dateA;
                case 'languages':
                    // Sort by number of languages
                    const langCountA = a.languages?.length || 0;
                    const langCountB = b.languages?.length || 0;
                    return order === 'asc' ? langCountA - langCountB : langCountB - langCountA;
                default:
                    return 0;
            }
        });
    }, [textFiles, order, orderBy]);

    const sortedAudioFiles = React.useMemo(() => {
        if (!audioFiles || audioFiles.length === 0) return [];

        return [...audioFiles].sort((a, b) => {
            switch (orderBy) {
                case 'name':
                    return order === 'asc'
                        ? (a.name || '').localeCompare(b.name || '')
                        : (b.name || '').localeCompare(a.name || '');
                case 'updatedBy':
                    return order === 'asc'
                        ? (a.updatedBy || 'Unknown').localeCompare(b.updatedBy || 'Unknown')
                        : (b.updatedBy || 'Unknown').localeCompare(a.updatedBy || 'Unknown');
                case 'updatedAt':
                    return order === 'asc'
                        ? new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime()
                        : new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
                default:
                    return 0;
            }
        });
    }, [audioFiles, order, orderBy]);

    const sortedBookFiles = React.useMemo(() => {
        if (!bookFiles || bookFiles.length === 0) return [];

        return [...bookFiles].sort((a, b) => {
            switch (orderBy) {
                case 'name':
                    return order === 'asc'
                        ? (a.name || '').localeCompare(b.name || '')
                        : (b.name || '').localeCompare(a.name || '');
                case 'updatedBy':
                    return order === 'asc'
                        ? (a.updatedBy || 'Unknown').localeCompare(b.updatedBy || 'Unknown')
                        : (b.updatedBy || 'Unknown').localeCompare(a.updatedBy || 'Unknown');
                case 'updatedAt':
                    return order === 'asc'
                        ? new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime()
                        : new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
                default:
                    return 0;
            }
        });
    }, [bookFiles, order, orderBy]);

    if (rowMode === 'task') {
        // Columns the server can sort by (keys match the view sort parameter)
        const sortable = new Set(['title', 'language', 'stage', 'assignee', 'due', 'pm', 'progress']);
        const toggleGroup = (key) =>
            setCollapsedGroups((prev) => {
                const next = new Set(prev);
                if (next.has(key)) {
                    next.delete(key);
                } else {
                    next.add(key);
                }
                return next;
            });
        if (!taskRows || taskRows.length === 0) {
            return (
                <Box sx={{ p: 4, textAlign: 'center', backgroundColor: 'background.paper', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                        {emptyTitle || 'Nothing here'}
                    </Typography>
                    {emptyMessage && (
                        <Typography variant="body2" component="div" sx={{ color: 'text.secondary' }}>
                            {emptyMessage}
                        </Typography>
                    )}
                </Box>
            );
        }
        return (
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        px: 2,
                        py: 0.5,
                        mb: 0.5,
                        backgroundColor: 'background.paper',
                        borderRadius: '8px',
                    }}
                >
                    {taskColumns.map((col) => {
                        const w = TASK_COLUMN_WIDTHS[col];
                        const flex = typeof w === 'number' ? `${w} 1 0` : `${w.md} 1 0`;
                        return (
                            <Box key={col} sx={{ flex, minWidth: 0, pl: col === 'title' ? (onTaskSelectToggle ? 9 : 4.5) : 0 }}>
                                {sortable.has(col) ? (
                                    <TableSortLabel
                                        active={orderBy === col}
                                        direction={orderBy === col ? order : 'asc'}
                                        onClick={() => handleRequestSort(col)}
                                    >
                                        <Typography variant="subtitle2">{TASK_COLUMN_LABELS[col]}</Typography>
                                    </TableSortLabel>
                                ) : (
                                    <Typography variant="subtitle2">{TASK_COLUMN_LABELS[col]}</Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
                {groupRows(taskRows, groups).map(({ group, rows }) => {
                    const key = group ? String(group.key) : '__all__';
                    const collapsed = collapsedGroups.has(key);
                    return (
                        <Box key={key} sx={{ mb: 1.5 }}>
                            {group && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <ButtonBase
                                        onClick={() => toggleGroup(key)}
                                        aria-expanded={!collapsed}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.5, borderRadius: 1 }}
                                    >
                                        {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {group.label} ({group.count})
                                        </Typography>
                                        {group.overdue > 0 && (
                                            <Typography variant="body2" color="error.main">
                                                {group.overdue} overdue
                                            </Typography>
                                        )}
                                        {upcomingLabel(group) && (
                                            <Typography variant="body2" color="text.secondary">
                                                {upcomingLabel(group)}
                                            </Typography>
                                        )}
                                    </ButtonBase>
                                    {onGroupOpen && group.key && (
                                        <Button size="small" onClick={() => onGroupOpen(group)}>
                                            {groupOpenLabel}
                                        </Button>
                                    )}
                                </Box>
                            )}
                            {!collapsed && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                    {rows.map((row) => (
                                        <TaskListItem
                                            key={rowKey(row)}
                                            row={row}
                                            columns={taskColumns}
                                            me={currentUser}
                                            onOpen={onTaskOpen}
                                            selected={onTaskSelectToggle ? !!selectedTaskIds?.has(row.id) : undefined}
                                            onSelectToggle={onTaskSelectToggle}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    }

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Help Overlay */}

            {/* Column headers - Only show if we have items */}
            {hasItems && (
                <Grid
                    container
                    sx={{
                        px: 1,
                        py: 0.5,
                        mb: 0.5,
                        backgroundColor: 'background.paper',
                        borderRadius: '8px',
                        display: { xs: 'none', sm: 'flex' }, // Hide on mobile
                        // Apply theme-aware color
                        color: 'text.primary',
                    }}
                >
                    <Grid item xs={12} sm={4} md={4}>
                        <TableSortLabel
                            active={orderBy === 'name'}
                            direction={orderBy === 'name' ? order : 'asc'}
                            onClick={() => handleRequestSort('name')}
                            sx={{ pl: 5 }}
                        >
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                Name
                            </Typography>
                        </TableSortLabel>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>Details</Box>
                        </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2} md={1}>
                        <TableSortLabel
                            active={orderBy === 'duration'}
                            direction={orderBy === 'duration' ? order : 'asc'}
                            onClick={() => handleRequestSort('duration')}
                        >
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <TimerIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                    Duration
                                </Box>
                            </Typography>
                        </TableSortLabel>
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                        <TableSortLabel
                            active={orderBy === 'updatedBy'}
                            direction={orderBy === 'updatedBy' ? order : 'asc'}
                            onClick={() => handleRequestSort('updatedBy')}
                        >
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                    Last Updated By
                                </Box>
                            </Typography>
                        </TableSortLabel>
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                        <TableSortLabel
                            active={orderBy === 'updatedAt'}
                            direction={orderBy === 'updatedAt' ? order : 'asc'}
                            onClick={() => handleRequestSort('updatedAt')}
                        >
                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <AccessTimeFilledIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                    Last Updated At
                                </Box>
                            </Typography>
                        </TableSortLabel>
                    </Grid>
                    <Grid item xs={6} sm={2} md={1}>
                        <Typography variant="subtitle2" align="center" sx={{ color: 'text.primary' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Actions</Box>
                        </Typography>
                    </Grid>
                </Grid>
            )}

            {/* Folders section */}
            {folders && folders.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                        Folders {folders.length > 0 && `(${folders.length})`}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {sortedFolders.map((folder, index) => (
                            <FolderListItem
                                key={`folder-${index}`}
                                className="file-folder-item"
                                folder={folder}
                                onFolderClick={onFolderClick}
                                onMenuOpen={onMenuOpen}
                                showPath={showPaths}
                                isDarkMode={theme.palette.mode === 'dark'}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Video Files section */}
            {videoFiles && videoFiles.length > 0 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                        Video Files {videoFiles.length > 0 && `(${videoFiles.length})`}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {sortedVideoFiles.map((file, index) => (
                            <FileListItem
                                key={`file-${index}`}
                                className="file-folder-item"
                                file={file}
                                onVideoFileClick={onVideoFileClick}
                                onMenuOpen={onMenuOpen}
                                showPath={showPaths}
                                type={FileTypes.VIDEO}
                                isDarkMode={theme.palette.mode === 'dark'}
                                isSelectMode={isSelectMode}
                                isSelected={selectedFiles.has(`video-${file.data}`)}
                                onSelectToggle={onSelectToggle}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Text Files section */}
            {textFiles && textFiles.length > 0 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                        Text Files {textFiles.length > 0 && `(${textFiles.length})`}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {sortedTextFiles.map((file, index) => (
                            <FileListItem
                                key={`textFile-${index}`}
                                className="file-folder-item"
                                file={file}
                                onTextFileClick={onTextFileClick}
                                onMenuOpen={onMenuOpen}
                                showPath={showPaths}
                                type={FileTypes.TEXT}
                                isDarkMode={theme.palette.mode === 'dark'}
                                isSelectMode={isSelectMode}
                                isSelected={selectedFiles.has(`text-${file.data}`)}
                                onSelectToggle={onSelectToggle}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Audio Files section */}
            {audioFiles && audioFiles.length > 0 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                        Audio Files {audioFiles.length > 0 && `(${audioFiles.length})`}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {sortedAudioFiles.map((file, index) => (
                            <FileListItem
                                key={`audioFile-${index}`}
                                className="file-folder-item"
                                file={file}
                                onAudioFileClick={onAudioFileClick}
                                onMenuOpen={onMenuOpen}
                                showPath={showPaths}
                                type={FileTypes.AUDIO}
                                isDarkMode={theme.palette.mode === 'dark'}
                                isSelectMode={isSelectMode}
                                isSelected={selectedFiles.has(`audio-${file.data}`)}
                                onSelectToggle={onSelectToggle}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Book Files section */}
            {bookFiles && bookFiles.length > 0 && (
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                        Books {bookFiles.length > 0 && `(${bookFiles.length})`}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {sortedBookFiles.map((file, index) => (
                            <FileListItem
                                key={`bookFile-${index}`}
                                className="file-folder-item"
                                file={file}
                                onBookFileClick={onBookFileClick}
                                onMenuOpen={onMenuOpen}
                                showPath={showPaths}
                                type={FileTypes.BOOK}
                                isDarkMode={theme.palette.mode === 'dark'}
                                isSelectMode={isSelectMode}
                                isSelected={selectedFiles.has(`book-${file.data}`)}
                                onSelectToggle={onSelectToggle}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Empty state */}
            {!hasItems && (
                <Box
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                        This folder is empty
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Create a new folder or upload files to get started
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ListView;
